import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CircleAlert, Image as ImageIcon, Loader2, RefreshCw, Upload } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames } from '@/lib/utils';
import {
  ESTADO_PLANTILLA, IDIOMAS, NOMBRE_IDIOMA,
  type Campana, type CampanaEditable, type HuecoPlantilla, type Idioma, type PlantillaResumen, type PreviaWhatsApp, type WhatsAppCampana,
} from '@/lib/comunicaciones';
import BurbujaWhatsApp from './BurbujaWhatsApp';

/**
 * Paso 2 de una campaña de WhatsApp: elegir una plantilla aprobada por Meta y
 * decir qué va en cada hueco. El texto de la plantilla no se puede cambiar
 * aquí (Meta la aprobó así); se cambia en WhatsApp Manager y vuelve a pasar
 * revisión.
 */
export default function PasoContenidoWhatsApp({ campana, onChange }: {
  campana: Campana;
  onChange: (cambios: Partial<CampanaEditable>) => void;
}) {
  const plantillas = useApi(() => api.getPlantillasWhatsApp(), []);
  const wa: WhatsAppCampana = campana.whatsapp || { plantilla: '', parametros: {}, cabeceraImagen: '' };
  const elegida = plantillas.data?.find(p => p.nombre === wa.plantilla);
  // Solo las del CRM para campañas: las de Hostify son para los huéspedes y las
  // «para leads» se mandan de una en una desde la ficha. La elegida se enseña
  // siempre, para que un borrador antiguo no la pierda de vista.
  const disponibles = (plantillas.data || []).filter(p => (p.delCrm && (p.uso || 'campanas') === 'campanas') || p.nombre === wa.plantilla);
  const cambiar = (w: Partial<WhatsAppCampana>) => onChange({ whatsapp: { ...wa, ...w } });

  const elegir = (p: PlantillaResumen) => {
    // Al cambiar de plantilla se conserva lo que encaje (mismo hueco); el nombre va por defecto en {{1}}
    const parametros: WhatsAppCampana['parametros'] = {};
    p.huecos.filter(h => !h.imagen).forEach((h, i) => {
      parametros[h.clave] = wa.parametros[h.clave] || (i === 0 && h.donde === 'cuerpo' ? { tipo: 'nombre', valor: '' } : { tipo: 'texto', valor: '' });
    });
    cambiar({ plantilla: p.nombre, parametros });
  };

  if (plantillas.error) {
    return (
      <div className="card text-sm space-y-2">
        <div className="text-red-600 flex items-center gap-1.5"><CircleAlert size={16} /> No se pudieron leer las plantillas de Meta: {plantillas.error}</div>
        <p className="text-sereno-dark/50">Revisa la conexión en <Link to="/comunicaciones/ajustes" className="underline">Comunicaciones → Ajustes</Link>.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
      <div className="space-y-4">
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sereno-dark">Plantilla</div>
            <button type="button" onClick={plantillas.refresh} className="btn-ghost text-xs flex items-center gap-1.5 px-2 py-1">
              {plantillas.loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Recargar
            </button>
          </div>
          <p className="text-xs text-sereno-dark/50">
            Las creadas desde el CRM y aprobadas por Meta. Las de Hostify no salen aquí: son para los huéspedes.{' '}
            <Link to="/comunicaciones/plantillas" className="underline">Crear una plantilla</Link>
          </p>
          {plantillas.loading && !plantillas.data ? <Loader2 size={18} className="animate-spin text-sereno-dark/30" /> : (
            <div className="space-y-1.5 max-h-72 overflow-auto">
              {disponibles.map(p => (
                <button
                  key={p.nombre}
                  type="button"
                  disabled={!p.aprobada}
                  onClick={() => elegir(p)}
                  className={classNames(
                    'w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center gap-2 disabled:opacity-40',
                    p.nombre === wa.plantilla ? 'border-sereno-dark bg-slate-50' : 'border-gray-200 hover:border-slate-300',
                  )}
                >
                  <span className="font-mono text-xs flex-1 truncate">{p.nombre}</span>
                  <span className={classNames('badge', p.categoria === 'MARKETING' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{p.categoria.toLowerCase()}</span>
                  <span className="text-[11px] text-sereno-dark/40 whitespace-nowrap">
                    {p.idiomas.map(i => (i.estado === 'APPROVED' ? i.idioma : `${i.idioma} ${(ESTADO_PLANTILLA[i.estado]?.texto || i.estado).toLowerCase()}`)).join(' · ')}
                  </span>
                </button>
              ))}
              {plantillas.data && disponibles.length === 0 && (
                <p className="text-sm text-sereno-dark/40">
                  Todavía no hay plantillas creadas desde el CRM. <Link to="/comunicaciones/plantillas" className="underline">Crea la primera</Link>.
                </p>
              )}
            </div>
          )}
          {elegida && elegida.categoria !== 'MARKETING' && (
            <div className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Es de categoría «{elegida.categoria.toLowerCase()}». Meta no permite usar plantillas de servicio para publicidad: si el contenido es promocional, puede bloquearlas o bajar la calidad del número.
            </div>
          )}
        </div>

        {elegida && (
          <div className="card space-y-3">
            <div className="font-semibold text-sereno-dark">Qué va en cada hueco</div>
            {elegida.huecos.length === 0 && <p className="text-sm text-sereno-dark/50">Esta plantilla no tiene huecos: sale tal cual.</p>}
            {elegida.huecos.map(h => (
              <Hueco key={h.clave} hueco={h} wa={wa} cambiar={cambiar} />
            ))}
            <label className="flex items-center gap-2 text-xs text-sereno-dark/50 pt-2 border-t border-gray-100">
              Quien no tenga la plantilla en su idioma recibe la de
              <select className="input py-1 text-xs w-auto" value={campana.idiomaReserva} onChange={e => onChange({ idiomaReserva: e.target.value as Idioma })}>
                {IDIOMAS.map(i => <option key={i} value={i}>{NOMBRE_IDIOMA[i]}</option>)}
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="xl:sticky xl:top-4">
        {elegida ? <VistaPreviaWhatsApp campana={campana} plantilla={elegida} /> : (
          <div className="card text-sm text-sereno-dark/40 text-center py-10">Elige una plantilla para ver cómo llega.</div>
        )}
      </div>
    </div>
  );
}

function Hueco({ hueco, wa, cambiar }: { hueco: HuecoPlantilla; wa: WhatsAppCampana; cambiar: (w: Partial<WhatsAppCampana>) => void }) {
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  if (hueco.imagen) {
    const subir = async (f?: File) => {
      if (!f) return;
      setSubiendo(true);
      setError(null);
      try {
        const { url } = await api.subirImagenCampana(f);
        cambiar({ cabeceraImagen: url });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo subir');
      } finally {
        setSubiendo(false);
        if (input.current) input.current.value = '';
      }
    };
    return (
      <div className="space-y-1.5">
        <div className="text-xs text-sereno-dark/50">Imagen de la cabecera</div>
        <div className="flex gap-2 items-center">
          <div className="w-16 h-12 rounded bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
            {wa.cabeceraImagen ? <img src={wa.cabeceraImagen} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-sereno-dark/30" />}
          </div>
          <button type="button" onClick={() => input.current?.click()} disabled={subiendo} className="btn-ghost border border-gray-200 text-xs flex items-center gap-1.5 px-3 py-1.5">
            {subiendo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {wa.cabeceraImagen ? 'Cambiar' : 'Subir imagen'}
          </button>
          <input ref={input} type="file" accept="image/jpeg,image/png" className="hidden" onChange={e => subir(e.target.files?.[0])} />
        </div>
        <p className="text-[11px] text-sereno-dark/35">JPG o PNG de hasta 5 MB (WhatsApp no admite GIF en la cabecera).</p>
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>
    );
  }

  const p = wa.parametros[hueco.clave] || { tipo: 'texto' as const, valor: '' };
  const fijar = (cambio: Partial<typeof p>) => cambiar({ parametros: { ...wa.parametros, [hueco.clave]: { ...p, ...cambio } } });
  const etiqueta = hueco.donde === 'botón' ? `Final del enlace del botón «${hueco.nombre}»` : `{{${hueco.nombre}}} · ${hueco.donde}`;

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-sereno-dark/50">{etiqueta}</div>
      <div className="flex gap-2">
        {hueco.donde !== 'botón' && (
          <select className="input w-auto text-sm" value={p.tipo} onChange={e => fijar({ tipo: e.target.value as 'nombre' | 'texto' })}>
            <option value="nombre">Nombre del destinatario</option>
            <option value="texto">Texto fijo</option>
          </select>
        )}
        <input
          className="input flex-1 text-sm"
          value={p.valor}
          onChange={e => fijar({ valor: e.target.value })}
          placeholder={p.tipo === 'nombre' ? 'Si no tiene nombre (p. ej. «viajero»)' : hueco.donde === 'botón' ? 'p. ej. casas?utm_source=whatsapp' : 'Texto'}
        />
      </div>
    </div>
  );
}

export function VistaPreviaWhatsApp({ campana, plantilla }: { campana: Campana; plantilla: PlantillaResumen }) {
  // Se empieza por quien habla español, que son casi todos, si la plantilla lo
  // tiene; si esa versión no está aprobada, la vista previa enseña la que
  // recibirían y lo dice.
  const [idioma, setIdioma] = useState<string>(plantilla.idiomas.some(i => i.idioma.toLowerCase().startsWith('es')) ? 'es' : campana.idiomaReserva);
  const [previa, setPrevia] = useState<{ idioma: string | null; previa: PreviaWhatsApp | null } | null>(null);
  const [cargando, setCargando] = useState(false);
  const clave = JSON.stringify([campana.whatsapp, idioma, campana.idiomaReserva]);

  useEffect(() => {
    if (!campana.whatsapp) return;
    let vigente = true;
    setCargando(true);
    const t = setTimeout(() => {
      api.vistaPreviaWhatsApp(campana.whatsapp!, idioma, campana.idiomaReserva)
        .then(r => { if (vigente) setPrevia(r); })
        .catch(() => { if (vigente) setPrevia(null); })
        .finally(() => { if (vigente) setCargando(false); });
    }, 400);
    return () => { vigente = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-sereno-dark/50 flex items-center gap-2">
          Vista previa {cargando && <Loader2 size={12} className="animate-spin" />}
        </div>
        <select className="input py-1 text-xs w-auto" value={idioma} onChange={e => setIdioma(e.target.value)}>
          {IDIOMAS.map(i => <option key={i} value={i}>Quien habla {NOMBRE_IDIOMA[i].toLowerCase()}</option>)}
        </select>
      </div>
      {previa?.previa ? (
        <>
          <BurbujaWhatsApp previa={previa.previa} />
          <p className="text-[11px] text-sereno-dark/40">Con «Laura» como nombre. Recibe la versión <span className="font-mono">{previa.idioma}</span> de la plantilla.</p>
        </>
      ) : previa && !previa.previa ? (
        <p className="text-sm text-red-600">La plantilla no tiene versión aprobada ni en ese idioma ni en el de reserva.</p>
      ) : null}
    </div>
  );
}
